<template>
  <div>
    <nav class="main-navigation bg-vpi-blue">
      <div class="mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-14">
          <div class="logo flex items-center uppercase text-base">
            <router-link
              class="logo flex items-center uppercase text-gray-450 text-base welcomeLink"
              to="/"
            >
              <img
                class="w-8 h-8"
                src="@/assets/svg/sentry-secure-logo.svg"
                alt="Sentry Secure Logo"
              />
              <span class="lg:inline text-white text-lg mx-4">Sentry-SECURE</span>
            </router-link>
            <span class="hidden lg:inline text-white text-lg ml-4">{{ portalType }}</span>
            <span class="hidden lg:inline text-white text-lg ml-4">
              {{ currentUserName }} {{ currentUserOrgConfig }}
            </span>
          </div>
          <div v-if="currentUserIsDispatch">
            <div class="hidden md:block">
              <div class="ml-10 flex">
                <router-link :class="`mt-1 block ${dispatcherLink}`" to="/event-dispatcher">
                  Dispatcher
                </router-link>
                <button :class="helpLink" @click="$emit('start-tour')">Help</button>
                <button :class="logoutLink" @click.prevent="logout()">Sign out</button>
              </div>
            </div>
          </div>
          <div v-else-if="currentUserIsImageViewer">
            <div class="hidden md:block">
              <div class="ml-10 flex">
                <button :class="helpLink" @click="$emit('start-tour')">Help</button>
                <button :class="logoutLink" @click.prevent="logout()">Sign out</button>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="hidden md:block">
              <div class="flex items-center">
                <button :class="helpLink" @click="$emit('start-tour')">Help</button>
                <router-link
                  v-for="routeDescription in headerRoutes"
                  :key="`${routeDescription.path}-header`"
                  :class="`${routeDescription.tutorial} ml-4`"
                  :to="routeDescription.path"
                >
                  {{ routeDescription.label }}
                </router-link>

                <button
                  class="ml-4 kabobMenu"
                  aria-label="Menu"
                  aria-haspopup="true"
                  @click.prevent="toggleDropdownMenu"
                >
                  <font-awesome-icon size="2x" :icon="['fal', 'ellipsis-v']" />
                </button>

                <div
                  v-if="dropdownOpen"
                  v-on-click-outside="closeDropdownMenu"
                  class="origin-top-right absolute right-0 w-48 shadow-xl z-20"
                  style="top: 64px"
                >
                  <div
                    class="px-3 pt-2 pb-4 bg-vpi-blue shadow-xs"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu"
                  >
                    <router-link
                      v-for="routeDescription in dropdownRoutes"
                      :key="`${routeDescription.path}-dropdown`"
                      :class="`${routeDescription.tutorial} mt-1 block`"
                      :to="routeDescription.path"
                    >
                      {{ routeDescription.label }}
                    </router-link>

                    <hr class="border-gray-450 my-2" />

                    <router-link
                      :class="`mt-1 block ${viewAccountLink}`"
                      :to="`/accounts/${currentAccountId}`"
                    >
                      Your Account
                    </router-link>
                    <button :class="changePasswordLink" @click.prevent="changeAccountPassword()">
                      Change Password
                    </button>
                    <router-link :class="`mt-1 block ${supportLink}`" :to="supportPath">
                      Support
                    </router-link>
                    <button :class="logoutLink" @click.prevent="logout()">Sign out</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="-mr-2 flex md:hidden">
            <!-- Mobile menu button -->
            <menu-icon :open="mobileMenuOpen" @toggle-menu="toggleMenu"></menu-icon>
          </div>
        </div>
      </div>

      <div v-if="mobileMenuOpen" class="md:hidden" :class="mobileMenuOpen ? 'block' : 'hidden'">
        <div class="p-3">
          <router-link
            v-for="routeDescription in routesToShow"
            :key="`${routeDescription.path}-mobile`"
            :class="`${routeDescription.tutorial} mt-1 block`"
            :to="routeDescription.path"
          >
            {{ routeDescription.label }}
          </router-link>
        </div>
        <div class="p-3 border-t border-gray-450">
          <div class="flex flex-col items-start">
            <router-link class="mt-1 block" :to="`/accounts/${currentOrgId}`">
              Your Account
            </router-link>
            <button :class="changePasswordLink" @click.prevent="changeAccountPassword">
              Change Password
            </button>

            <button :class="logoutLink" @click.prevent="logout()">Sign out</button>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import MenuIcon from '@/components/icons/MenuIcon.vue';
import * as Auth from '@/modules/authv2';
import { generateTourSteps, tourStepsTargets } from '@/tours/tourStepsNav';

// Vue setup
defineEmits(['start-tour']);

const store = useStore();
const route = useRoute();

// Constants
const helpLink = tourStepsTargets.helpLink;
const logoutLink = tourStepsTargets.logoutLink;
const dispatcherLink = tourStepsTargets.dispatcherLink;
const viewAccountLink = tourStepsTargets.viewYourAccountLink;
const changePasswordLink = tourStepsTargets.changePasswordLink;
const supportLink = tourStepsTargets.supportLink;

// Reactive state
const dropdownOpen = ref(false);
const dropdownLocked = ref(false);

const mobileMenuOpen = ref(false);

// Computed properties
const orgType = computed(() => store.getters.currentUserOrgType);
const isConsumer = computed(() => store.getters.currentUserIsConsumer);
const isOwner = computed(() => store.getters.currentUserIsOwner);
const currentOrgId = computed(() => store.getters.currentUserOrgId);
const currentAccountId = computed(() => store.getters.currentUserAccountId);
const currentUserName = computed(() => store.getters.currentUserName);
const currentUserOrgConfig = computed(() => {
  const userConfig = store.getters.config;
  return userConfig?.Name;
});
const currentUserIsDispatch = computed(() => store.getters.currentUserIsDispatcher);
const currentUserIsImageViewer = computed(() => store.getters.currentUserIsImageViewer);
const supportPath = computed(() => `/support?source=${route.path}`);
const portalType = computed(() => {
  return currentUserIsImageViewer.value
    ? 'Image Viewer'
    : currentUserIsDispatch.value
      ? 'Dispatch'
      : orgType.value + ' Administration';
});

// All possible routes and configurations that can be navigated to from the main navigation
// Possible configuration fields:
// inHeader: true - show in header bar
//   ownerOnly: true - only show to Owner organizations
//   consumerOnly: true - only show to Consumer organizations

// prettier-ignore
const allRoutes = computed(() => [
  { label: 'Devices',             path: '/devices',                             tutorial: tourStepsTargets.devicesLink,      inHeader: true, ownerOnly: true },
  { label: 'Users',               path: '/users',                               tutorial: tourStepsTargets.appUsersLink,     inHeader: true },
  { label: 'Connections',         path: '/connections',                         tutorial: tourStepsTargets.connectionsLink,  inHeader: true },
  { label: 'Events',              path: '/events',                              tutorial: tourStepsTargets.eventsLink,       inHeader: true },
  { label: 'Accounts',            path: '/accounts',                            tutorial: tourStepsTargets.accountsLink,     inHeader: true },
  { label: 'Downloads',           path: '/downloads',                           tutorial: tourStepsTargets.downloadsLink },
  { label: 'Instructions',        path: '/instructions',                        tutorial: tourStepsTargets.instructionsLink },
  { label: 'Training Events',     path: '/training-create',                     tutorial: tourStepsTargets.trainingLink },
  { label: 'Training Scenarios',  path: '/training-scenarios',                  tutorial: tourStepsTargets.trainingScenariosLink },
  { label: 'Banners and Emails',  path: '/nonRMSEvents',                        tutorial: tourStepsTargets.nonRMSEventsLink },
  { label: 'Configuration',       path: `/configuration/${currentOrgId.value}`, tutorial: tourStepsTargets.configurationLink },
  { label: 'Conditional Users',   path: '/users/conditions',                    tutorial: tourStepsTargets.conditionalUsersLink },
  { label: 'Sub Groups',          path: '/users/subgroups',                     tutorial: tourStepsTargets.subgroupsLink,    consumerOnly: true },
  { label: 'Dispatcher',          path: '/event-dispatcher',                    tutorial: tourStepsTargets.dispatcherLink,   consumerOnly: true,  },
  { label: 'CAD Config',          path: '/backend-subscriber-configuration',    tutorial: tourStepsTargets.cadLink,          consumerOnly: true,  },
  { label: 'Guided SSO Setup',    path: '/sso',                                 tutorial: tourStepsTargets.ssoLink,          consumerOnly: true },
  { label: 'Summary',             path: '/summary',                             tutorial: tourStepsTargets.summaryLink,      ownerOnly: true },
]);

const consumerRoutes = computed(() => allRoutes.value.filter((route) => route.ownerOnly !== true));
const ownerRoutes = computed(() => allRoutes.value.filter((route) => route.consumerOnly !== true));

const routesToShow = computed(() => {
  if (isOwner.value) {
    return ownerRoutes.value;
  } else if (isConsumer.value) {
    return consumerRoutes.value;
  }

  return [];
});

const headerRoutes = computed(() => routesToShow.value.filter((route) => route.inHeader === true));

const dropdownRoutes = computed(() =>
  routesToShow.value.filter((route) => route.inHeader !== true)
);

// Watchers
watch(route, () => {
  console.log('Route changed, closing dropdown menu');
  dropdownOpen.value = false;
});

// Methods
const toggleDropdownMenu = () => {
  console.log('Toggling dropdown menu, old value :', dropdownOpen.value);
  dropdownOpen.value = !dropdownOpen.value;
};

const openDropdownMenu = () => {
  console.log('Opening dropdown menu');
  dropdownOpen.value = true;
};

const closeDropdownMenu = () => {
  if (!dropdownLocked.value) {
    console.log('Closing dropdown menu');
    dropdownOpen.value = false;
  } else {
    console.log('Dropdown menu is locked open, not closing');
  }
};

const toggleMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const logout = () => {
  store.dispatch('logoutCurrentUser');
};

const openAndLockMenu = () => {
  dropdownLocked.value = true;
  openDropdownMenu();
};

const closeAndUnlockMenu = () => {
  dropdownLocked.value = false;
  closeDropdownMenu();
};

const changeAccountPassword = () => {
  Auth.changePassword();
};

const initializeTourSteps = () => {
  const steps = generateTourSteps({
    isConsumer: isConsumer.value,
    isOwner: isOwner.value,
    isImageViewer: currentUserIsImageViewer.value,
    isDispatcher: store.getters.currentUserIsDispatcher,
    openAndLockMenu,
    closeAndUnlockMenu,
  });

  store.commit('setTourStepsNav', steps);
};

// Lifecycle hooks
onMounted(() => {
  store.dispatch('getConfig');
  initializeTourSteps();
});
</script>

<style scoped>
@import '@/assets/css/app.css';

a,
button {
  @apply p-2 text-base font-medium text-gray-450 cursor-pointer;

  &:hover,
  &:focus,
  &.router-link-active {
    @apply text-white outline-none;
  }

  &.logo {
    @apply text-gray-600;
  }
}

@media (min-width: 640px) {
  @apply text-sm;
}

@media (min-width: 1024px) {
  @apply text-base;
}
</style>
